import { IPublicSession } from 'state/session';
import { sessionVehicleSchema, type ISessionVehicle, type IVehicle, type IVehicleId } from 'data/schema';
import { isEqual } from 'utils/deepEqual';

/**
 * Tracks session keys whose local edit has not yet been confirmed by the server (AVS-4330).
 *
 * Two things consume this:
 *  - the remote-apply chokepoint, so an incoming payload never overwrites an edit that is
 *    still in flight (a read-repair legitimately carries a NEWER version, so the staleness
 *    gate alone cannot protect a pending local change);
 *  - the sync layer, which keeps keys dirty when a PUT fails so they can be re-sent instead
 *    of being silently lost.
 */

let dirtyKeys = new Set<keyof IPublicSession>();

/**
 * The fields of each registry entry a pending `vehicles` edit changed (AVS-4470). The `vehicles`
 * key alone cannot say which entry or field is pending, and an edit to an inactive entry marks no
 * root key at all. Lives and clears with the `vehicles` key.
 */
let dirtyEntryFields = new Map<IVehicleId, Set<keyof ISessionVehicle>>();

const SESSION_VEHICLE_KEYS = Object.keys(sessionVehicleSchema.shape) as (keyof ISessionVehicle)[];
const NO_FIELDS: ReadonlySet<keyof ISessionVehicle> = new Set();

export function markDirty(keys: Iterable<keyof IPublicSession>): void {
  for (const key of keys) {
    dirtyKeys.add(key);
  }
}

export function getDirtyKeys(): ReadonlySet<keyof IPublicSession> {
  return dirtyKeys;
}

/**
 * Clear only the keys a completed request actually carried (its snapshot). A key first dirtied
 * AFTER the snapshot is not in `keys`, so it survives and is re-sent by a later request. A key
 * that WAS in the snapshot but is re-edited mid-flight cannot be distinguished from its older
 * generation (this is a plain Set), so it IS cleared here — harmless under full-body PUTs (the
 * next PUT re-sends the whole session) but a lost-update risk if `deltaSessionSync` is enabled
 * (Gate I9). Distinguishing generations would require a Map<key, gen>, deferred with that gate.
 */
export function clearDirty(keys: Iterable<keyof IPublicSession>): void {
  for (const key of keys) {
    dirtyKeys.delete(key);
    if (key === 'vehicles') {
      dirtyEntryFields = new Map();
    }
  }
}

export function resetDirtyKeys(): void {
  dirtyKeys = new Set();
  dirtyEntryFields = new Map();
}

/** Record which entry fields a local `vehicles` edit changed. Removed entries have nothing pending. */
export function markChangedEntryFields(
  oldVehicles: Record<IVehicleId, IVehicle> | undefined,
  newVehicles: Record<IVehicleId, IVehicle> | undefined
): void {
  for (const [id, vehicle] of Object.entries(newVehicles ?? {})) {
    const previous = oldVehicles?.[id];
    if (previous === vehicle) {
      continue;
    }
    const changed = SESSION_VEHICLE_KEYS.filter((key) => !isEqual(previous?.[key], vehicle[key]));
    if (changed.length > 0) {
      const fields = dirtyEntryFields.get(id) ?? new Set();
      changed.forEach((key) => fields.add(key));
      dirtyEntryFields.set(id, fields);
    }
  }
}

export function getDirtyEntryFields(id: IVehicleId): ReadonlySet<keyof ISessionVehicle> {
  return dirtyEntryFields.get(id) ?? NO_FIELDS;
}

export function clearDirtyEntry(id: IVehicleId): void {
  dirtyEntryFields.delete(id);
}
